import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { Menu, X, ChevronDown, BookOpen, Layers, TrendingUp, Globe, Smartphone, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
const hrajbonusLogo = "/img/hrajbonus-logo.webp";

type NavChild = { label: string; href: string; icon: React.ElementType; desc: string };
type NavItem = {
  label: string;
  href: string;
  children?: NavChild[];
};

const navItems: NavItem[] = [
  {
    label: "Kasina",
    href: "/kasina",
    children: [
      { label: "Zahraniční kasina", href: "/kasina/zahranicni", icon: Globe, desc: "Kasina s mezinárodní licencí pro české hráče" },
      { label: "Mobilní kasina", href: "/kasina/mobilni", icon: Smartphone, desc: "Kasina optimalizovaná pro mobilní zařízení" },
    ],
  },
  {
    label: "Poker",
    href: "/poker",
    children: [
      { label: "Jak hrát poker",      href: "/poker/jak-hrat",   icon: BookOpen,   desc: "Pravidla, herní průběh a typy sázek" },
      { label: "Pokerové kombinace",  href: "/poker/kombinace",  icon: Layers,     desc: "Pořadí karet a nejsilnejsi kombinace" },
      { label: "Pokerové strategie",  href: "/poker/strategie",  icon: TrendingUp, desc: "TAG styl, pozice a bankroll management" },
    ],
  },
  { label: "Porovnat", href: "/porovnat" },
  { label: "Bonus", href: "/bonusy" },
  {
    label: "Automaty",
    href: "/automaty",
    children: [
      { label: "Jak vyhrát na automatech", href: "/automaty/jak-vyhrat", icon: Zap, desc: "RTP, volatilita, strategie a tipy pro maximální výhru" },
      { label: "Sloty které vyplácejí nejvíce", href: "/automaty/sloty-ktere-vyplaceji-nejvice", icon: TrendingUp, desc: "Nejlépe vyplácející online sloty a jak je poznat" },
    ],
  },
  { label: "O nás", href: "/o-nas" },
];

function DropdownItem({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!item.children) {
    return (
      <a
        href={item.href}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className="px-4 py-2 text-[#003da5] hover:text-[#002d7c] text-sm rounded-md transition-colors duration-150 hover:bg-blue-50 font-bold"
      >
        {item.label}
      </a>
    );
  }

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <a
        href={item.href}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className="flex items-center gap-1 px-4 py-2 text-[#003da5] hover:text-[#002d7c] text-sm rounded-md transition-colors duration-150 hover:bg-blue-50 font-bold"
      >
        {item.label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </a>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
          style={{ minWidth: "300px" }}
        >
          {/* caret */}
          <div
            className="absolute left-1/2 -translate-x-1/2 top-[6px] w-3 h-3 rotate-45 rounded-sm"
            style={{ background: "#001a4d", border: "1px solid rgba(255,255,255,0.12)" }}
          />
          <div
            className="dropdown-panel relative rounded-2xl overflow-hidden"
            style={{
              background: "linear-gradient(160deg, #001a4d 0%, #002d7c 100%)",
              boxShadow: "0 20px 48px rgba(0,20,80,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
            }}
          >
            <div className="p-2">
              {item.children.map((child) => {
                const Icon = child.icon;
                return (
                  <a
                    key={child.href}
                    href={child.href}
                    className="group flex items-start gap-3 px-3 py-3 rounded-xl transition-all duration-150 hover:bg-white/10"
                    data-testid={`nav-link-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div
                      className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5"
                      style={{ background: "rgba(255,255,255,0.12)" }}
                    >
                      <Icon className="w-4 h-4 text-white/80" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-white font-semibold text-sm leading-tight">{child.label}</span>
                      <span className="text-white/50 text-xs leading-snug mt-0.5">{child.desc}</span>
                    </div>
                  </a>
                );
              })}
            </div>
            <div
              className="px-5 py-3 border-t"
              style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.15)" }}
            >
              <a
                href={item.href}
                className="text-xs text-white/40 hover:text-white/70 transition-colors font-medium"
              >
{item.label === "Poker" ? "Všechno o pokeru" : item.label === "Automaty" ? "Všechny automaty" : `Všechna ${item.label.toLowerCase()}`} &rarr;
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [location, navigate] = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMobileClose = () => {
    setMobileOpen(false);
    setMobileDropdownOpen(null);
  };

  const handleCTAClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    handleMobileClose();
    if (location === "/") {
      document.getElementById("rankings")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        document.getElementById("rankings")?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white"
      style={{
        boxShadow: scrolled ? "0 2px 16px rgba(0,0,0,0.10)" : "0 1px 0 #e5e7eb",
      }}
      data-testid="header"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="flex items-center justify-between h-16 gap-4">
          <a href="/" className="flex items-center flex-shrink-0" data-testid="link-logo">
            <img src={hrajbonusLogo} alt="HrajBonus" className="h-12 w-auto object-contain" width="192" height="48" fetchpriority="high" />
          </a>

          <nav className="hidden md:flex items-center gap-1" data-testid="nav-desktop">
            {navItems.map((item) => (
              <DropdownItem key={item.label} item={item} />
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a href="/#rankings" onClick={handleCTAClick} data-testid="button-cta-header">
              <Button
                size="sm"
                className="hraj-btn text-white font-semibold px-5"
                style={{ background: "#003da5" }}
              >
                Nejlepší kasina
              </Button>
            </a>
          </div>

          <button
            className="md:hidden text-gray-700 p-2 rounded-md hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            data-testid="button-mobile-menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white" data-testid="nav-mobile">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => {
              if (!item.children) {
                return (
                  <a
                    key={item.label}
                    href={item.href}
                    data-testid={`mobile-link-${item.label.toLowerCase()}`}
                    className="px-4 py-3 text-[#003da5] hover:text-[#002d7c] text-sm font-bold rounded-md transition-colors hover:bg-blue-50"
                    onClick={handleMobileClose}
                  >
                    {item.label}
                  </a>
                );
              }

              return (
                <div key={item.label}>
                  <div className="flex items-center">
                    <a
                      href={item.href}
                      className="flex-1 px-4 py-3 text-[#003da5] hover:text-[#002d7c] text-sm font-bold rounded-l-md transition-colors hover:bg-blue-50"
                      onClick={handleMobileClose}
                      data-testid={`mobile-link-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                    </a>
                    <button
                      onClick={() => setMobileDropdownOpen(mobileDropdownOpen === item.label ? null : item.label)}
                      className="px-3 py-3 text-[#003da5] hover:bg-blue-50 rounded-r-md transition-colors"
                      data-testid={`button-mobile-${item.label.toLowerCase()}-expand`}
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileDropdownOpen === item.label ? "rotate-180" : ""}`} />
                    </button>
                  </div>

                  {mobileDropdownOpen === item.label && (
                    <div className="ml-4 mb-1 flex flex-col gap-0.5">
                      {item.children.map((child) => (
                        <a
                          key={child.href}
                          href={child.href}
                          className="px-4 py-2.5 text-[#003da5] text-sm font-semibold rounded-md transition-colors hover:bg-blue-50 border-l-2 border-[#003da5]/20"
                          onClick={handleMobileClose}
                          data-testid={`mobile-link-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {child.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-2 pb-1">
              <a href="/#rankings" onClick={handleCTAClick} data-testid="button-cta-mobile">
                <Button
                  className="hraj-btn w-full text-white font-semibold"
                  style={{ background: "#003da5" }}
                >
                  Nejlepší kasina
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
